import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";
import { Html5Qrcode } from "html5-qrcode";
import {
  getCameraErrorMessage,
  requestCameraAccess,
} from "../../helper/requestCameraAccess";
import "./QrScannerScreen.scss";

const getQrBoxSize = (viewfinderWidth, viewfinderHeight) => {
  const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.72);
  return { width: size, height: size };
};

const startScannerWithFallback = async (scanner, onDecode) => {
  const scanConfig = {
    fps: 15,
    qrbox: getQrBoxSize,
    aspectRatio: 1,
    disableFlip: false,
  };

  const cameraConfigs = [{ facingMode: "environment" }, { facingMode: "user" }];

  for (const cameraConfig of cameraConfigs) {
    try {
      await scanner.start(cameraConfig, scanConfig, onDecode, () => {});
      return;
    } catch {
      // Try next camera configuration.
    }
  }

  try {
    const cameras = await Html5Qrcode.getCameras();
    if (cameras.length) {
      const backCamera =
        cameras.find((camera) => /back|rear|environment/i.test(camera.label)) ||
        cameras[cameras.length - 1];

      await scanner.start(backCamera.id, scanConfig, onDecode, () => {});
      return;
    }
  } catch {
    // Fall through to generic video constraint.
  }

  await scanner.start({ facingMode: { ideal: "environment" } }, scanConfig, onDecode, () => {});
};

export const QrScannerScreen = ({ open, onClose, onScan }) => {
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const fileInputRef = useRef(null);
  const isStartingRef = useRef(false);
  const [error, setError] = useState("");

  onScanRef.current = onScan;

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;

    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      scanner.clear();
    } catch {
      // Ignore cleanup errors when camera is already stopped.
    } finally {
      scannerRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (isStartingRef.current || scannerRef.current?.isScanning) return;

    setError("");
    isStartingRef.current = true;

    try {
      await requestCameraAccess();

      const scanner = new Html5Qrcode("qr-reader", { verbose: false });
      scannerRef.current = scanner;

      await startScannerWithFallback(scanner, (decodedText) => {
        stopScanner().finally(() => onScanRef.current(decodedText));
      });
    } catch (err) {
      setError(getCameraErrorMessage(err));
    } finally {
      isStartingRef.current = false;
    }
  }, [stopScanner]);

  useLayoutEffect(() => {
    if (!open) {
      setError("");
      isStartingRef.current = false;
      stopScanner();
      return undefined;
    }

    const timer = window.setTimeout(() => {
      startCamera();
    }, 50);

    return () => {
      window.clearTimeout(timer);
      stopScanner();
    };
  }, [open, startCamera, stopScanner]);

  const handleClose = async () => {
    await stopScanner();
    setError("");
    onClose();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const scanner = new Html5Qrcode("qr-reader-file", { verbose: false });
    try {
      const decodedText = await scanner.scanFile(file, true);
      onScanRef.current(decodedText);
    } catch {
      setError("Could not read QR from image. Try another photo.");
    } finally {
      event.target.value = "";
    }
  };

  if (!open) return null;

  return (
    <Box className="qr-scanner-screen">
      <Box className="qr-scanner-topbar">
        <FaArrowLeft className="back-icon" onClick={handleClose} />
        <Typography variant="h6" fontWeight={600}>
          Scan QR Code
        </Typography>
      </Box>

      <Box className="qr-scanner-viewport">
        <Box id="qr-reader" />
        <Box id="qr-reader-file" sx={{ display: "none" }} />

        <Box className="qr-scanner-overlay">
          <Box className="qr-scanner-frame">
            <span className="qr-corner qr-corner-tl" />
            <span className="qr-corner qr-corner-tr" />
            <span className="qr-corner qr-corner-bl" />
            <span className="qr-corner qr-corner-br" />
          </Box>
        </Box>
      </Box>

      <Box className="qr-scanner-footer">
        {error ? (
          <>
            <Typography className="qr-scanner-error">{error}</Typography>
            <Button
              variant="outlined"
              className="qr-scanner-retry-btn"
              onClick={startCamera}
            >
              Retry Camera
            </Button>
            <Button
              className="qr-scanner-upload-link"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload QR from gallery
            </Button>
          </>
        ) : (
          <Typography className="qr-scanner-hint">
            Align the QR code within the frame to scan
          </Typography>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleFileUpload}
        />
      </Box>
    </Box>
  );
};
